import { FormSection } from "../commons/components/form/FormWithSections";
import FormTitle from "../commons/components/form/FormTitle";
import * as React from "react";
import { useState } from "react";
import { InsuranceInfo } from "./InsuranceInfo";
import Field, { FieldKey } from "../commons/model/form/Field";
import Form from "../commons/components/form/Form";
import sfetch from "../commons/utils/super-fetch";
import AsyncAction from "../AsyncAction";
import { SectionConfiguration } from "../commons/components/form/SectionConfiguration";
import { getValue } from "../commons/utils/form-utils";
import BinaryRadioButton from "../commons/components/buttons/BinaryRadioButton";
import { getCountryFromPhoneNumberOrDefault } from "../commons/utils/phoneNumber";
import InputState from "../commons/model/form/InputState";
import FileInput from "../commons/components/form/input/FileInput";
import Country from "../commons/model/address/Country";
import RemoveAction from "../commons/components/action/RemoveAction";
import TextAction from "../commons/components/action/TextAction";

class FormValue {
  primary: { [key: string]: InputState };
  secondary: { [key: string]: InputState };
}

export default function DualInsuranceInfoForm({
  primaryInsuranceInfo,
  secondaryInsuranceInfo,
  fields,
  availableCountries,
  defaultCountry,
  updateUrl,
  title,
  submitLabel,
  afterSubmit,
  supportedTypes,
}: {
  primaryInsuranceInfo?: InsuranceInfo;
  secondaryInsuranceInfo?: InsuranceInfo;
  fields: Field[];
  availableCountries: Country[];
  defaultCountry?: string;
  updateUrl: string;
  title?: string;
  submitLabel?: string;
  afterSubmit?: (form: { [key: string]: InputState }) => any;
  supportedTypes: string[];
}) {
  const [formValue, setFormValue] = useState<FormValue>({
    primary: {},
    secondary: {},
  });
  const [hasInsurance, setHasInsurance] = useState<boolean>(
    primaryInsuranceInfo ? primaryInsuranceInfo.hasInsurance : true
  );
  const [hasSecondaryInsurance, setHasSecondaryInsurance] =
    useState<boolean>(!!secondaryInsuranceInfo);

  const [errorMessage, setErrorMessage] = useState(null);

  function preloadInsuranceCardImage(state: InputState, insuranceInfo: InsuranceInfo) {
    if (insuranceInfo && insuranceInfo.insuranceCardUrl && insuranceInfo.insuranceCardFileName) {
      FileInput.preloadImage(
        state,
        insuranceInfo.insuranceCardFileName,
        insuranceInfo.insuranceCardUrl
      );
    }
  }

  const submitInsuranceInformation = async (form: { [key: string]: InputState }) => {
    const body = new FormData();
    body.set("hasInsurance", hasInsurance.toString());
    if (hasInsurance) {
      fields.forEach((field) => {
        ["primary.", "secondary."]
          .filter((prefix) => hasSecondaryInsurance || prefix != "secondary.")
          .forEach((prefix) => {
            const fieldName = prefix + field.name;
            const value = getValue(form[fieldName]);
            if (value) {
              body.set(fieldName, value.file ?? value);
            }
          });
      });
    }
    try {
      await sfetch(updateUrl, {
        method: "PUT",
        body: body,
      });

      if (afterSubmit) afterSubmit(form);
    } catch (error) {
      setErrorMessage(i18n["Global.TechnicalError"]);
      throw error;
    }
  };

  const sections: SectionConfiguration[] = [
    {
      title: i18n["Form.InsuranceInfo.Section.SubscriberInformation"],
      fieldKeys: [
        FieldKey.FIRST_NAME,
        FieldKey.MIDDLE_NAME,
        FieldKey.LAST_NAME,
        FieldKey.BIRTH_DATE,
        FieldKey.SSN,
      ],
    },
    {
      title: i18n["Form.InsuranceInfo.Section.Status"],
      fieldKeys: [FieldKey.EMPLOYER, FieldKey.INSURANCE_ID, FieldKey.EMPLOYER_ID],
    },
    {
      title: i18n["Form.InsuranceInfo.Section.InsuranceInformation"],
      fieldKeys: [
        FieldKey.COMPANY_NAME,
        FieldKey.STATE,
        FieldKey.COMPANY_PHONE,
        FieldKey.COMPANY_GROUP_NUMBER,
        FieldKey.INSURANCE_CARD,
      ],
    },
  ];

  const getExtraProps = (prefix: string, insuranceInfo: InsuranceInfo) => {
    return {
      [prefix + "companyName"]: {
        additionalClass: "big",
      },
      [prefix + "state"]: {
        additionalClass: "small",
      },
      [prefix + "companyPhoneNumber"]: {
        selectedCountry: getCountryFromPhoneNumberOrDefault(
          availableCountries,
          insuranceInfo?.companyPhoneNumber,
          defaultCountry
        ),
        availableCountries,
        additionalClass: "medium",
      },
      [prefix + "companyGroupNumber"]: {
        additionalClass: "medium",
      },
      [prefix + "insuranceCard"]: {
        uploadLabel: i18n["Form.Field.Title.UploadInsuranceCard"],
        additionalClass: "full-width",
        accept: supportedTypes.join(","),
        errorMessage: i18n["Form.InsuranceInfo.InsuranceCard.Error"],
      },
    };
  };

  const getInitialValueAndExtraProps = (
    prefix: string,
    fields: Field[],
    initialValues: InsuranceInfo
  ) => {
    const extraProps = getExtraProps(prefix, initialValues);
    return fields.reduce((acc, field) => {
      const initialValue = findInsuranceInformation(field.key, initialValues);
      acc[field.name] = {
        initialValue,
        ...extraProps[field.name],
      };
      return acc;
    }, {});
  };

  const getInsuranceInfoFields = (
    title: string,
    prefix: string,
    initialValues: InsuranceInfo,
    removeAction: () => void = undefined
  ) => {
    const mappedFields = fields.map((field) => {
      return { ...field, ["name"]: prefix + field.name };
    });
    const initialValueAndExtraProps = getInitialValueAndExtraProps(
      prefix,
      mappedFields,
      initialValues
    );
    return (
      <>
        <h2>
          <div>{i18n[title]}</div>
          {removeAction && <RemoveAction onClick={removeAction} />}
        </h2>
        {sections.map((section, index) => {
          return (
            <FormSection title={section.title} number={index + 1} key={section.title}>
              {Form.getFieldComponents(
                mappedFields.filter((field) => section.fieldKeys.includes(field.key)),
                initialValueAndExtraProps,
                FieldKey.getTitle
              )}
            </FormSection>
          );
        })}
      </>
    );
  };

  const splitPrefix = (name: string) => {
    const [prefix, suffix] = name.split(".");
    switch (prefix) {
      case "primary":
      case "secondary":
        return [prefix, suffix];
      default:
        console.error(`Invalid component name: ${name}, prefix=${prefix}, name=${suffix}`);
        throw new Error();
    }
  };

  function setFieldInputState(prefix: string, name: string, inputState: InputState) {
    setFormValue((formValue) => {
      return {
        ...formValue,
        [prefix]: { ...formValue[prefix], [name]: inputState },
      };
    });
  }

  const onUpdate = (changedInput: InputState) => {
    const [prefix, name] = splitPrefix(changedInput.name);
    if (name == "insuranceCard" && !formValue[prefix]?.insuranceCard && !changedInput.value) {
      if (prefix == "primary" && primaryInsuranceInfo?.insuranceCardUrl) {
        preloadInsuranceCardImage(changedInput, primaryInsuranceInfo);
      } else if (prefix == "secondary" && secondaryInsuranceInfo?.insuranceCardUrl) {
        preloadInsuranceCardImage(changedInput, secondaryInsuranceInfo);
      }
    } else {
      setFieldInputState(prefix, name, changedInput);
    }
  };

  const removeFirstInsurance = () => {
    setHasSecondaryInsurance(false);
    Object.entries(formValue.secondary).forEach(([fieldName, inputState]) => {
      formValue.primary[fieldName].setValue(inputState.value);
    });
  };

  return (
    <>
      {title && <FormTitle title={title} />}
      <div className={"react-form"}>
        {errorMessage && <div className={"form-general-error message danger"}>{errorMessage}</div>}
        <BinaryRadioButton
          name={"hasInsurance"}
          labelTrue={i18n["Form.InsuranceInfo.Field.HaveInsurance"]}
          labelFalse={i18n["Form.InsuranceInfo.Field.DontHaveInsurance"]}
          checked={hasInsurance}
          onChange={setHasInsurance}
          additionalClass={"full-width"}
        />
        <hr className={"hr-secondary"} />
      </div>
      {hasInsurance ? (
        <Form
          submitLabel={submitLabel ? submitLabel : i18n["Global.Save"]}
          submit={submitInsuranceInformation}
          onUpdate={onUpdate}
          className="insurance-info-form"
        >
          {getInsuranceInfoFields(
            "Form.InsuranceInfo.PrimaryInsuranceInfoHeader",
            "primary.",
            primaryInsuranceInfo,
            hasSecondaryInsurance ? removeFirstInsurance : null
          )}
          {!hasSecondaryInsurance && (
            <TextAction
              icon="ic_plus"
              additionalClass="add-insurance"
              label={i18n["Form.InsuranceInfo.AddSecondaryInsurance"]}
              onClick={() => {
                setHasSecondaryInsurance(true);
              }}
            />
          )}
          {hasSecondaryInsurance &&
            getInsuranceInfoFields(
              "Form.InsuranceInfo.SecondaryInsuranceInfoHeader",
              "secondary.",
              secondaryInsuranceInfo,
              () => setHasSecondaryInsurance(false)
            )}
        </Form>
      ) : (
        <div className={"h-flex centered action-container"}>
          <AsyncAction
            buttonLabel={submitLabel ? submitLabel : i18n["Global.Save"]}
            promise={() => submitInsuranceInformation(null)}
            onError={() => this.props.displayError(i18n["Global.Status.technical_error"])}
            buttonType={"button-primary"}
          />
        </div>
      )}
    </>
  );

  function findInsuranceInformation(key: FieldKey, insuranceInfo: InsuranceInfo) {
    switch (key) {
      case FieldKey.FIRST_NAME:
        return insuranceInfo?.firstName;
      case FieldKey.LAST_NAME:
        return insuranceInfo?.lastName;
      case FieldKey.BIRTH_DATE:
        return insuranceInfo?.birthDate ? new Date(insuranceInfo?.birthDate) : null;
      case FieldKey.MIDDLE_NAME:
        return insuranceInfo?.middleName;
      case FieldKey.SSN:
        return insuranceInfo?.socialSecurityNumber;
      case FieldKey.EMPLOYER:
        return insuranceInfo?.employer;
      case FieldKey.INSURANCE_ID:
        return insuranceInfo?.insuranceId;
      case FieldKey.EMPLOYER_ID:
        return insuranceInfo?.employerId;
      case FieldKey.COMPANY_NAME:
        return insuranceInfo?.companyName;
      case FieldKey.STATE:
        return insuranceInfo?.state;
      case FieldKey.COMPANY_PHONE:
        return insuranceInfo?.companyPhoneNumber;
      case FieldKey.COMPANY_GROUP_NUMBER:
        return insuranceInfo?.companyGroupNumber;
      default:
        return null;
    }
  }
}
